import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { clubInfo } from "@/lib/mbti";
import LibraryEditor from "@/components/LibraryEditor";
import { Library, FileText, Music, Link2, Search, GraduationCap, CalendarCheck, Megaphone, Pencil, Trash2, Plus, FolderOpen, Star } from "lucide-react";

const CLUBS = ["Diplomatas", "Analistas", "Sentinelas", "Exploradores"];
const abs = (url) => (url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);
const fmtDate = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("pt-PT", { day: "numeric", month: "long", year: "numeric" });
const ICON = { audio: Music, link: Link2 };
const KIND = {
  topic: [FolderOpen, "Tema", "#22C55E"], communique: [Megaphone, "Comunicado", "#3B82F6"],
  training: [GraduationCap, "Formação", "#EAB308"], meeting: [CalendarCheck, "Reunião", "#A3A3A3"],
};

const StarBtn = ({ on, onClick, token }) => (
  <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }} title={on ? "Tirar dos favoritos" : "Marcar como favorito"}
    className={`p-1 shrink-0 transition-transform hover:scale-110 ${on ? "text-[#EAB308]" : "text-[#444] hover:text-[#EAB308]"}`}
    data-testid={`library-fav-${token}`}>
    <Star className="w-4 h-4" fill={on ? "#EAB308" : "none"} />
  </button>
);

const FileRow = ({ f, fav, onFav }) => {
  const Icon = ICON[f.kind] || FileText;
  if (f.kind === "audio") {
    return (
      <div className="bg-[#0F0F0F] border border-[#1F1F1F] px-4 py-3" data-testid={`library-file-${f.token}`}>
        <div className="text-xs text-[#A3A3A3] mb-2 flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 text-[#E50000]" /> <span className="flex-1 truncate">{f.filename}</span>
          <StarBtn on={fav} onClick={onFav} token={f.token} />
        </div>
        <audio controls preload="none" src={abs(f.url)} className="w-full h-9" />
      </div>
    );
  }
  return (
    <a href={abs(f.url)} target="_blank" rel="noopener noreferrer" data-testid={`library-file-${f.token}`}
      className="flex items-center gap-3 bg-[#0F0F0F] border border-[#1F1F1F] px-4 py-3 text-sm hover:border-[#E50000] transition-colors">
      <Icon className="w-4 h-4 text-[#E50000] shrink-0" />
      <span className="truncate flex-1">{f.filename}</span>
      <StarBtn on={fav} onClick={onFav} token={f.token} />
    </a>
  );
};

const KindTag = ({ kind }) => {
  const [Icon, label, color] = KIND[kind] || KIND.meeting;
  return <span className="text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1" style={{ color }}><Icon className="w-3 h-3" /> {label}</span>;
};

export default function StudyLibrary() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState(null);
  const [editing, setEditing] = useState(null);
  const { user } = useAuth();
  const canEdit = ["admin", "staff", "coach"].includes(user?.role);

  const load = () => api.get("/library").then((r) => setData(r.data)).catch(() => setData({ items: [], favorites: [] }));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (data && tab === null) setTab(CLUBS.includes(data.my_club) ? data.my_club : "all"); }, [data, tab]);

  const favs = new Set(data?.favorites || []);
  const toggleFav = async (token) => {
    setData((d) => ({ ...d, favorites: favs.has(token) ? d.favorites.filter((t) => t !== token) : [...d.favorites, token] }));
    try { await api.post(`/library/favorites/${token}`); } catch (err) { toast.error(formatApiError(err)); load(); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Apagar "${s.title}" da biblioteca? Os emails já enviados não mudam.`)) return;
    try { await api.delete(`/library/${s.source}/${s.id}`); toast.success("Apagado da biblioteca"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const items = data?.items || [];
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return items.filter((s) => (tab === "all" || !tab || s.clubs.includes(tab)) && (!t ||
      [s.title, s.trainer_name, ...s.files.map((f) => f.filename)].some((v) => (v || "").toLowerCase().includes(t))));
  }, [items, q, tab]);
  const favFiles = items.flatMap((s) => s.files.filter((f) => favs.has(f.token)).map((f) => ({ ...f, from: s.title })))
    .filter((f, i, arr) => arr.findIndex((x) => x.token === f.token) === i);
  const order = [...(CLUBS.includes(data?.my_club) ? [data.my_club] : []), ...CLUBS.filter((c) => c !== data?.my_club)];
  const count = (c) => items.filter((s) => s.clubs.includes(c)).length;

  return (
    <div className="space-y-8" data-testid="library-page">
      <div>
        <div className="kw-overline mb-2 flex items-center gap-2"><Library className="w-3.5 h-3.5" /> Recursos</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight">
          Biblioteca de <span className="text-[#E50000]">estudo</span>
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-3 max-w-2xl">Todo o material das reuniões e formações — PDFs, áudios e links — para reveres quando quiseres. Marca com a estrela o que mais usas.</p>
      </div>

      {favFiles.length > 0 && (
        <div className="kw-card p-6 border-l-2 border-[#EAB308]" data-testid="library-favorites">
          <div className="kw-overline text-[#EAB308] mb-4 flex items-center gap-2"><Star className="w-3.5 h-3.5" fill="#EAB308" /> Os meus favoritos</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {favFiles.map((f) => (
              <div key={f.token}>
                <FileRow f={f} fav onFav={() => toggleFav(f.token)} />
                <div className="text-[10px] uppercase tracking-widest text-[#555] mt-1 truncate">{f.from}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {editing === "new" && (
        <div className="kw-card p-6 max-w-3xl"><div className="kw-overline mb-4">Novo tema</div>
          <LibraryEditor onClose={() => setEditing(null)} onDone={load} /></div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="library-club-tabs">
        {order.map((c) => {
          const on = tab === c;
          const color = clubInfo(c)?.color || "#E50000";
          return (
            <button key={c} onClick={() => setTab(c)} data-testid={`library-tab-${c}`}
              className="text-[10px] uppercase tracking-widest font-bold px-4 py-2 border transition-colors"
              style={on ? { borderColor: color, color: "#0A0A0A", background: color } : { borderColor: "#333", color }}>
              {c === data?.my_club ? `O meu clube · ${c}` : c} ({count(c)})
            </button>
          );
        })}
        <button onClick={() => setTab("all")} data-testid="library-tab-all"
          className={`text-[10px] uppercase tracking-widest font-bold px-4 py-2 border transition-colors ${tab === "all" ? "bg-white text-black border-white" : "border-[#333] text-[#A3A3A3]"}`}>
          Todos ({items.length})
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1 min-w-[260px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
          <input className="kw-input !pl-9" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Procurar sessão, tema ou ficheiro" data-testid="library-search" />
        </div>
        {canEdit && editing !== "new" && (
          <button onClick={() => setEditing("new")} className="kw-btn-primary !py-2.5 text-[10px] inline-flex items-center gap-1.5"
            data-testid="library-new-topic"><Plus className="w-3 h-3" /> Novo tema</button>
        )}
      </div>

      {data && shown.length === 0 && (
        <p className="text-sm text-[#737373]" data-testid="library-empty">Ainda não há material aqui.</p>
      )}
      <div className="space-y-5">
        {shown.map((s) => (
          <div key={s.id} className="kw-card p-6 kw-fade-up" data-testid={`library-session-${s.id}`}>
            <div className="flex flex-wrap items-center gap-3 mb-1">
              <KindTag kind={s.kind} />
              <span className="text-[10px] uppercase tracking-widest text-[#737373]">{fmtDate(s.date)}</span>
              <span className="text-[10px] uppercase tracking-widest text-[#555] flex-1">
                {s.clubs.length === 4 ? "Todos os clubes" : s.clubs.join(" · ")}
              </span>
              {canEdit && editing !== s.id && (
                <>
                  <button onClick={() => setEditing(s.id)} title="Editar" className="text-[#737373] hover:text-white p-1"
                    data-testid={`library-edit-${s.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(s)} title="Apagar da biblioteca" className="text-[#737373] hover:text-[#E50000] p-1"
                    data-testid={`library-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                </>
              )}
            </div>
            {editing === s.id ? (
              <div className="mt-3"><LibraryEditor item={s} onClose={() => setEditing(null)} onDone={load} /></div>
            ) : (
              <>
                <div className="font-display text-2xl font-black uppercase tracking-tight">{s.title}</div>
                {s.trainer_name && <div className="text-xs text-[#A3A3A3] mt-1">com {s.trainer_name}</div>}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4">
                  {s.files.map((f) => <FileRow key={f.token} f={f} fav={favs.has(f.token)} onFav={() => toggleFav(f.token)} />)}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
